import { useRef } from 'react';
import type { KeyboardEvent, PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useChatStore } from '../hooks/useChatStore';
import {
  PANEL_DEFAULT_WIDTH,
  PANEL_MAX_WIDTH,
  PANEL_MIN_WIDTH,
  clampPanelWidth,
} from '../lib/panelWidth';

const KEY_STEP = 16;
const KEY_STEP_LARGE = 64;

export function ChatResizeHandle() {
  const { t } = useTranslation();
  const panelWidth = useChatStore((state) => state.panelWidth);
  const setPanelWidth = useChatStore((state) => state.setPanelWidth);
  const drag = useRef<{ startX: number; startWidth: number }>(undefined);

  const resizeTo = (width: number) =>
    setPanelWidth(clampPanelWidth(width, window.innerWidth));

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    const startWidth =
      event.currentTarget.parentElement?.getBoundingClientRect().width ??
      panelWidth;
    drag.current = { startX: event.clientX, startWidth };
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (drag.current) {
      resizeTo(drag.current.startWidth + drag.current.startX - event.clientX);
    }
  };

  const stopDrag = () => {
    drag.current = undefined;
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? KEY_STEP_LARGE : KEY_STEP;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      resizeTo(panelWidth + step);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      resizeTo(panelWidth - step);
    }
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={t('domain.ai.chat.resize')}
      aria-valuenow={panelWidth}
      aria-valuemin={PANEL_MIN_WIDTH}
      aria-valuemax={PANEL_MAX_WIDTH}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={stopDrag}
      onPointerCancel={stopDrag}
      onKeyDown={onKeyDown}
      onDoubleClick={() => resizeTo(PANEL_DEFAULT_WIDTH)}
      className="absolute inset-y-0 -left-1 z-10 hidden w-2 cursor-col-resize touch-none select-none hover:bg-neutrals-v2-400 focus-visible:bg-brand-blue-100 focus-visible:outline-none md:block"
    />
  );
}
